import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import type { OnyxChip } from '@onyx/angular';
import {
  OnyxAddressComponent,
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
} from '@onyx/angular';
import { orderBy } from 'lodash';
import { catchError, EMPTY, startWith, Subject, switchMap, tap } from 'rxjs';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { RouteHelper } from '../../../../../common/helpers/route.helper';
import {
  ROUTE_CONTEXT,
  RouteService,
} from '../../../../../common/services/route.service';
import { ModalsStore } from '../../../../../common/stores/modals.store';
import { TelematicsStore } from '../../../../../common/stores/telematics.store';
import { TechnicalPointCategory } from '../../../../fleet/common/enums/technical-point-category';
import { SwapPointType } from '../../../../fleet/common/enums/technical-point-type';
import { FleetService } from '../../../../fleet/common/services/fleet.service';
import { SwapModalHelper } from '../../helpers/swap/swap-modal.helper';
import type { Swap } from '../../interfaces/swap/swap';
import { SwapModalDriverReportComponent } from './swap-modal-driver-report/swap-modal-driver-report.component';
import { SwapModalNoteComponent } from './swap-modal-note/swap-modal-note.component';
import { SwapModalRouteComponent } from './swap-modal-route/swap-modal-route.component';
import { SwapModalSummaryComponent } from './swap-modal-summary/swap-modal-summary.component';

export interface SwapModalData {
  uuid: string;
  vehicleUuid: string | null;
}

enum SwapModalSection {
  SUMMARY,
  ROUTE,
  DRIVER_REPORT,
  NOTE,
}

@Component({
  selector: 'app-swap-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconComponent,
    OnyxAddressComponent,
    OnyxChipsComponent,
    SwapModalNoteComponent,
    SwapModalDriverReportComponent,
    SwapModalSummaryComponent,
    OnyxIconButtonComponent,
    OnyxDropdownDirective,
    SwapModalRouteComponent,
  ],
  providers: [{ provide: ROUTE_CONTEXT, useValue: 'map' }, RouteService],
  templateUrl: './swap-modal.component.html',
  styleUrl: './swap-modal.component.scss',
})
export class SwapModalComponent implements OnInit {
  protected readonly data = inject<SwapModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly telematicsStore = inject(TelematicsStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fleetService = inject(FleetService);
  private readonly modalsStore = inject(ModalsStore);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly swapModalHelper = inject(SwapModalHelper);

  protected readonly I18N = 'map.swapModal';

  protected readonly SwapModalSection = SwapModalSection;

  protected swap = signal<Swap | null>(null);

  protected section = this.modalsStore.getActiveSection(
    SwapModalComponent.name,
    () => this.sections(),
  );
  protected loading = signal(false);
  protected error = signal(false);
  protected swap$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected isDouble = computed(
    () => this.swap()?.type === SwapPointType.DOUBLE,
  );
  protected sections = computed<OnyxChip<SwapModalSection>[]>(() => [
    { name: 'labels.summary', value: SwapModalSection.SUMMARY },
    { name: 'labels.route', value: SwapModalSection.ROUTE },
    ...(this.swap()?.isRecorded ||
    this.technicalPoints().some(
      ({ driverReports }) =>
        driverReports?.waiting ||
        driverReports?.['in-progress'] ||
        driverReports?.completed,
    )
      ? [
          {
            name: 'labels.driverReport',
            value: SwapModalSection.DRIVER_REPORT,
          },
        ]
      : []),
    { name: 'labels.note', value: SwapModalSection.NOTE },
  ]);
  protected technicalPoints = computed(() => {
    const swap = this.swap();
    if (!swap?.technicalPoints?.length) return [];

    const vehicleSwapPoint = swap.vehicleRoute?.find(
      (routePoint) =>
        RouteHelper.getCategory(routePoint) ===
        TechnicalPointCategory.SWAP_POINT,
    );
    if (!vehicleSwapPoint) return swap.technicalPoints;

    return orderBy(
      swap.technicalPoints,
      (point) => point.uuid === RouteHelper.getUuid(vehicleSwapPoint),
      'desc',
    );
  });
  protected vehicleUuid = computed(() => {
    const swap = this.swap();
    if (!swap) return null;

    const telematicsItems = this.telematicsStore
      .telematicsItemsBySwapUuid()
      .get(this.data.uuid);
    const vehicleUuid = telematicsItems?.find(
      (item) => item.uuid === swap.vehicle.uuid,
    )?.uuid;

    return this.data.vehicleUuid ?? vehicleUuid ?? null;
  });
  protected options = computed(() =>
    this.swap()
      ? this.swapModalHelper.getOptions(this.swap()!, this.close$)
      : [],
  );
  protected showOnMapOptions = computed(() => {
    const swap = this.swap();
    if (!swap) return null;

    return this.swapModalHelper.getShowOnMapOptions(swap, (vehicleUuid) =>
      this.showOnMap(vehicleUuid),
    );
  });
  protected showOnGanttOptions = computed(() => {
    const swap = this.swap();
    if (!swap) return null;

    return this.swapModalHelper.getShowOnGanttOptions(swap, (vehicleUuid) =>
      this.showOnGantt(vehicleUuid),
    );
  });

  public ngOnInit(): void {
    this.swap$
      .pipe(
        startWith(undefined),
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.fleetService.getSwap(this.data.uuid).pipe(
            catchError(() => {
              this.swap.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((swap) => {
        this.swap.set(swap);
        this.loading.set(false);
        this.error.set(false);
      });

    this.fleetService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.swap$.next());
  }

  protected showOnMap(uuid?: string): void {
    const vehicleUuid = uuid ?? this.vehicleUuid();
    if (!vehicleUuid) return;

    CommonHelper.showOnMap(this.router, vehicleUuid, this.injector);
    this.close$.next();
  }

  protected showOnGantt(uuid?: string): void {
    const vehicleUuid = uuid ?? this.vehicleUuid();
    if (!vehicleUuid) return;

    CommonHelper.showOnGantt(this.router, 'vehicle', vehicleUuid);
    this.close$.next();
  }

  protected share(): void {
    const swap = this.swap();
    if (swap) this.swapModalHelper.share(swap.uuid);
  }
}
